'use client';

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import styles from './seasonal.module.css';

/**
 * Animated layers shared by the seasonal banners and popups (Halloween 2026,
 * Black Month 2026). Each piece is the flattened artwork of the designer's
 * handoff (public/previews/popups-handoff) with these layers on top; the
 * motion values are the handoff's, see seasonal.module.css.
 *
 * Positions and sizes are given in design px (1920-wide desktop frames,
 * 400-wide mobile frames) and rendered in rem, since 1rem = 8px at the
 * design width (app/globals.scss).
 */

export const rem = (px: number): string => `${+(px / 8).toFixed(5)}rem`;

/* -------------------------------------------------------------------------- */
/*  One requestAnimationFrame loop for every flame and pumpkin on the page    */
/* -------------------------------------------------------------------------- */

type Tick = (t: number, now: number) => void;
const subscribers = new Set<Tick>();
let frame = 0;

function loop(now: number) {
  const t = now / 1000;
  subscribers.forEach((fn) => fn(t, now));
  frame = subscribers.size ? requestAnimationFrame(loop) : 0;
}

function subscribe(fn: Tick): () => void {
  subscribers.add(fn);
  if (!frame) frame = requestAnimationFrame(loop);
  return () => {
    subscribers.delete(fn);
    if (!subscribers.size && frame) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
  };
}

const frac = (x: number) => x - Math.floor(x);

/**
 * Opacity of a flame glow at time t (seconds) for a layer seed: a slow
 * breath plus two faster flickers, and every ~9 s it almost goes out and
 * comes back (handoff "Chamas piscando").
 */
export function flameOpacity(t: number, seed: number): number {
  let v =
    0.62 +
    0.18 * Math.sin(t * 1.9 + seed) +
    0.12 * Math.sin(t * 5.3 + seed * 2.1) +
    0.08 * Math.sin(t * 11.7 + seed * 0.7);
  const c = frac(t * 0.11 + seed * 0.37);
  if (c < 0.05) v *= 0.12 + c * 6;
  return Math.max(0, Math.min(1, v));
}

/* -------------------------------------------------------------------------- */
/*  Flame glow                                                                 */
/* -------------------------------------------------------------------------- */

interface FlameGlowProps {
  src: string;
  className?: string;
  style?: CSSProperties;
  /** Fixed seed, so two glows never flicker in sync. Random when omitted. */
  seed?: number;
  /** Entrance: the flame stays out this long after mount, then lights up over 700 ms. */
  delayMs?: number;
}

/**
 * `glow@2x.webp` of the handoff: the lit parts of the artwork on black,
 * screen-blended over it, with its opacity driven per frame. Reduced motion
 * keeps the flames on at 80%.
 */
export function FlameGlow({ src, className, style, seed, delayMs = 0 }: FlameGlowProps) {
  const ref = useRef<HTMLImageElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reduced) {
      el.style.opacity = '0.8';
      return;
    }
    const s = seed ?? Math.random() * 100;
    const on = performance.now() + delayMs;
    el.style.opacity = '0';
    return subscribe((t, now) => {
      if (now < on) return;
      // First 700 ms: ramps up while sputtering (random 1 / 0.3 per frame).
      const k = Math.min(1, (now - on) / 700);
      const sputter = k < 1 ? (Math.random() > 0.5 ? 1 : 0.3) : 1;
      el.style.opacity = String(flameOpacity(t, s) * k * sputter);
    });
  }, [reduced, seed, delayMs]);

  return (
    // eslint-disable-next-line @next/next/no-img-element -- decorative plate
    <img
      ref={ref}
      src={src}
      alt=""
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 h-full w-full max-w-none object-cover mix-blend-screen',
        className,
      )}
      style={{ opacity: 0, ...style }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/*  Fog (Halloween)                                                            */
/* -------------------------------------------------------------------------- */

/** Two tiles of `nevoa.webp` drifting along the bottom in opposite directions. */
export function Fog({ className }: { className?: string }) {
  return (
    <>
      <div aria-hidden className={cn(styles.fog, className)} />
      <div aria-hidden className={cn(styles.fog, styles.fogB, className)} />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Silver beam (Black Month)                                                  */
/* -------------------------------------------------------------------------- */

interface BeamProps {
  src: string;
  className?: string;
  style?: CSSProperties;
  /** Play the popup entrance (comes down from -40px, 1400 ms, 350 ms late). */
  entrance?: boolean;
}

/** Three stacked copies of `beam@2x.webp`: the swaying base and two sweeping sheens. */
export function Beam({ src, className, style, entrance }: BeamProps) {
  return (
    <div
      aria-hidden
      className={cn(styles.beam, entrance && styles.beamIn, className)}
      style={style}
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
      <img src={src} alt="" className={styles.beamBase} />
      {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
      <img src={src} alt="" className={styles.beamSheen} />
      {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
      <img src={src} alt="" className={cn(styles.beamSheen, styles.beamSheen2)} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Loose pumpkin (Halloween header)                                           */
/* -------------------------------------------------------------------------- */

export interface PumpkinBox {
  /** Design px, from the frame's left edge (or right edge when `fromRight`). */
  x: number;
  y: number;
  width: number;
  height: number;
  fromRight?: boolean;
}

interface FloatingPumpkinProps {
  src: string;
  glowSrc?: string;
  box: PumpkinBox;
  /** Order of appearance: staggers the entrance and seeds the float. */
  index: number;
  /** Turns in 3D as well as floating (the handoff spins the wide pumpkin only). */
  spin?: boolean;
  entrance?: boolean;
  className?: string;
}

/**
 * `pumpkin-N.webp` + its glow, floating up and down (and turning when
 * `spin`). The entrance comes in from the side the pumpkin sits on.
 */
export function FloatingPumpkin({
  src,
  glowSrc,
  box,
  index,
  spin = false,
  entrance = false,
  className,
}: FloatingPumpkinProps) {
  const inner = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const el = inner.current;
    if (!el || reduced) return;
    const seed = index * 1.7;
    return subscribe((t) => {
      const y = Math.sin(t * 0.9 + seed) * 8;
      el.style.transform = spin
        ? `perspective(700px) translateY(${y}px) rotateY(${Math.sin(t * 0.8 + seed) * 42}deg)`
        : `translateY(${y}px)`;
    });
  }, [reduced, spin, index]);

  const position: CSSProperties = box.fromRight
    ? { right: rem(box.x), top: rem(box.y) }
    : { left: rem(box.x), top: rem(box.y) };

  return (
    <div
      aria-hidden
      className={cn('pointer-events-none absolute', entrance && styles.pumpkinIn, className)}
      style={
        {
          ...position,
          width: rem(box.width),
          height: rem(box.height),
          '--in-delay': `${450 + index * 140}ms`,
          '--in-x': box.fromRight ? '220px' : '-220px',
        } as CSSProperties
      }
    >
      <div ref={inner} className="absolute inset-0 will-change-transform">
        {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
        <img src={src} alt="" className="absolute inset-0 h-full w-full max-w-none" />
        {glowSrc && <FlameGlow src={glowSrc} className="object-fill" seed={index * 13.7} />}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  "BLACK MONTH" satin ribbon (Black Month)                                   */
/* -------------------------------------------------------------------------- */

export interface RibbonPlacement {
  /** Centre of the ribbon in design px of the piece (or from the right edge when `fromRight`). */
  cx: number;
  cy: number;
  fromRight?: boolean;
  width: number;
  /** Height of the ribbon band in design px; the canvas container is three times that. */
  height: number;
  /** CSS rotation in degrees (positive = clockwise). */
  angle: number;
  opacity?: number;
}

interface RibbonLayerProps {
  placement: RibbonPlacement;
  /** Popup entrance: comes in from outside, 1200 ms, 650 ms + 160 per ribbon. */
  entrance?: boolean;
  index?: number;
  /** Width/height of the piece in design px, to pick the entrance direction. */
  frame: { width: number; height: number };
  className?: string;
}

type Ribbon = { destroy(): void; set(next: Record<string, unknown>): void };

/** Rendered-rem to design-px ratio: 1 at the design width (1rem = 8px). */
function useRemScale(): number {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const measure = () =>
      setScale(parseFloat(getComputedStyle(document.documentElement).fontSize) / 8 || 1);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return scale;
}

/**
 * The animated "BLACK MONTH • BLACK MONTH" ribbon of the handoff
 * (lib/ribbonType.js, three.js), loaded on the client only when a Black Month
 * piece renders. The canvas is laid out in design px and scaled with the rem
 * scale, so the wave, the band and the type look the same at every viewport
 * width (the engine sizes its world from the container's px). The static
 * Figma export of the ribbon stands in under reduced motion and when WebGL
 * is unavailable.
 */
export function RibbonLayer({ placement, entrance, index = 0, frame, className }: RibbonLayerProps) {
  const host = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const scale = useRemScale();
  const [fallback, setFallback] = useState(false);
  const { cx, cy, fromRight, width, height, angle, opacity = 0.9 } = placement;
  const boxHeight = height * 3;

  useEffect(() => {
    const el = host.current;
    if (!el || reduced) return;
    let ribbon: Ribbon | null = null;
    let cancelled = false;
    (async () => {
      try {
        const [{ createRibbonType, RIBBON_DEFAULTS }] = await Promise.all([
          import('@/lib/ribbonType'),
          document.fonts?.ready ?? Promise.resolve(),
        ]);
        if (cancelled) return;
        ribbon = createRibbonType(el, {
          ...RIBBON_DEFAULTS,
          text: 'BLACK MONTH',
          separator: ' • ',
          colors: ['#EEF6F4', '#0A0B0B'],
          ribbons: 1,
          band: true,
          ribbonWidth: height * (height < 80 ? 0.62 : 0.32),
          textScale: 0.6,
          // The canvas reads the container's font-family: next/font hashes
          // the family name, so 'Geist' alone would not resolve.
          fontFamily: 'inherit',
          fontWeight: 800,
          letterSpacing: 0,
          path: 'wave',
          amplitude: height * 0.06,
          frequency: 0.5,
          depth: 18,
          twist: 0.05,
          twistSpeed: 0.08,
          speed: 70,
          direction: 'left',
          flow: 0.25,
          tilt: 0,
          perspective: 28,
          shading: 0.35,
          sheen: 0.5,
          draggable: false,
          clickTwist: false,
          hitTest: false,
          autoTwist: 4 + Math.random() * 3,
          intro: false,
          hoverSpeed: 0,
        }) as Ribbon;
      } catch {
        if (!cancelled) setFallback(true);
      }
    })();
    return () => {
      cancelled = true;
      ribbon?.destroy();
    };
  }, [reduced, height]);

  const position: CSSProperties = fromRight
    ? { right: rem(cx - width / 2), top: rem(cy - boxHeight / 2) }
    : { left: rem(cx - width / 2), top: rem(cy - boxHeight / 2) };
  const showStatic = reduced || fallback;

  return (
    <div
      aria-hidden
      className={cn('pointer-events-none absolute', entrance && styles.layerIn, className)}
      style={
        {
          ...position,
          width: rem(width),
          height: rem(boxHeight),
          '--in-delay': `${650 + index * 160}ms`,
          '--in-x': `${(fromRight ? frame.width - cx : cx) < frame.width / 2 ? -320 : 320}px`,
          '--in-y': `${cy < frame.height / 2 ? -220 : 220}px`,
        } as CSSProperties
      }
    >
      <div className="absolute inset-0" style={{ transform: `rotate(${angle}deg)`, opacity }}>
        {showStatic ? (
          // eslint-disable-next-line @next/next/no-img-element -- decorative
          <img
            src="/seasonal/black-month/ribbon.png"
            alt=""
            className="absolute left-0 max-w-none"
            style={{ top: rem(height), width: rem(width), height: rem(height) }}
          />
        ) : (
          <div
            ref={host}
            className="absolute left-0 top-0 font-geist [&_canvas]:absolute [&_canvas]:inset-0 [&_canvas]:h-full [&_canvas]:w-full"
            style={{
              width,
              height: boxHeight,
              transform: `scale(${scale})`,
              transformOrigin: '0 0',
            }}
          />
        )}
      </div>
    </div>
  );
}
